/**
 * CONFIG_OVER_CODE — client-side reflection.
 *
 * The server resolves the full cascade (defaults → env → `app_config` table
 * → clamps) and serves the precomputed result in `GET /api/config`. We **do not
 * reimplement the cascade here**: that would invite drift with zero benefit.
 *
 * What does live here, and is essential, is **safe fallback**.
 * CashSync is local-first: the entire app must work without network. If
 * `/api/config` does not respond —or returns something unexpected— the client keeps
 * running with code defaults, which are EXACTLY the historical literals and
 * the same as `builtin_defaults()` in Rust.
 *
 * Hard domain rule: nothing here touches amounts or balances. Thresholds
 * decide **when to warn**, the recurring cap determines **how many occurrences
 * to materialize at once**, freshness decides **when to mark a rate as stale**,
 * and caps control **how many ads**. None of the four enters into the arithmetic
 * of already recorded money.
 */

/** 100% of a budget: natural cap of a consumption percentage. */
const FULL_PERCENT = 100;
/** Historical WARNING threshold: 80% of budget consumed. */
const WARN_PERCENT = 80;
/** Milliseconds in a second (config speaks in s, ad gate in ms). */
const MS_PER_SECOND = 1000;
/** Length of an ISO date key `YYYY-MM-DD`. */
const ISO_DATE_LENGTH = 10;
/** Milliseconds in a day (UTC, no DST: keys are dates, not instants). */
const MS_PER_DAY = 86_400_000;

/** Domain parameters (finance) served by the platform. */
export interface FinancePublicConfig {
  /** Consumption percentages triggering alerts (100 = 100%), descending. */
  budgetAlertThresholdPcts: number[];
  /** Cap on occurrences a recurring rule materializes upon catch-up. */
  recurringMaxCatchupOccurrences: number;
  /** Days after which an exchange rate is considered stale. `0` = never warn. */
  exchangeRateMaxAgeDays: number;
}

/** Ad frequency capping served by the platform. */
export interface AdCapPublicConfig {
  interstitialPerSession: number;
  interstitialPerDay: number;
  minGapBetweenInterstitialsSecs: number;
  rewardsPerDayPerType: number;
}

export interface PublicAppConfig {
  i18n: { availableLocales: string[] };
  modules: { p2pEnabled: boolean };
  finance: FinancePublicConfig;
  ads: AdCapPublicConfig;
}

/**
 * Client CODE defaults. Byte-for-byte mirror of `builtin_defaults()`
 * (Rust) and existing literals:
 * `BUDGET_ALERT_THRESHOLDS = [1, 0.8]`, `MAX_CATCHUP_OCCURRENCES = 366`,
 * `DEFAULT_AD_CAP_POLICY`. Without server, the app behaves as usual.
 */
export const DEFAULT_PUBLIC_APP_CONFIG: PublicAppConfig = {
  i18n: { availableLocales: [] },
  modules: { p2pEnabled: true },
  finance: {
    budgetAlertThresholdPcts: [FULL_PERCENT, WARN_PERCENT],
    recurringMaxCatchupOccurrences: 366,
    exchangeRateMaxAgeDays: 0,
  },
  ads: {
    interstitialPerSession: 1,
    interstitialPerDay: 3,
    minGapBetweenInterstitialsSecs: 600,
    rewardsPerDayPerType: 3,
  },
};

/**
 * Type guard (not an assertion): narrows to indexable object without lying to the
 * compiler. What arrives from network is truly `unknown`, so we check,
 * not assert.
 */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function section(parent: Record<string, unknown>, key: string): Record<string, unknown> {
  const child = parent[key];
  return isRecord(child) ? child : {};
}

function finiteInt(value: unknown, fallback: number, min = 0): number {
  return typeof value === "number" && Number.isFinite(value) && value >= min
    ? Math.trunc(value)
    : fallback;
}

function stringList(value: unknown, fallback: string[]): string[] {
  if (!Array.isArray(value)) {
    return fallback;
  }
  return value.filter((v): v is string => typeof v === "string" && v.trim() !== "");
}

/**
 * Normalizes what arrives from the server against code defaults.
 *
 * **Field by field, not all or nothing**: a response missing a key
 * (older server, mid-deployment) cannot tear down the rest of configuration.
 * Each invalid field falls back to its default while the others are preserved.
 */
export function mergePublicAppConfig(remote: unknown): PublicAppConfig {
  const d = DEFAULT_PUBLIC_APP_CONFIG;
  if (!isRecord(remote)) {
    return d;
  }
  const finance = section(remote, "finance");
  const ads = section(remote, "ads");
  const i18n = section(remote, "i18n");
  const modules = section(remote, "modules");

  // Thresholds: only meaningful percentages, deduplicated and in
  // DESCENDING order — consumer takes the HIGHEST crossed threshold, so order
  // carries semantics, not cosmetics. If none valid remain, fallback to default.
  const rawPcts = Array.isArray(finance.budgetAlertThresholdPcts)
    ? finance.budgetAlertThresholdPcts
    : [];
  const pcts = [
    ...new Set(
      rawPcts.filter(
        (p): p is number =>
          typeof p === "number" && Number.isFinite(p) && p >= 1 && p <= FULL_PERCENT,
      ),
    ),
  ].toSorted((a, b) => b - a);

  return {
    i18n: { availableLocales: stringList(i18n.availableLocales, d.i18n.availableLocales) },
    modules: {
      p2pEnabled:
        typeof modules.p2pEnabled === "boolean" ? modules.p2pEnabled : d.modules.p2pEnabled,
    },
    finance: {
      budgetAlertThresholdPcts: pcts.length > 0 ? pcts : d.finance.budgetAlertThresholdPcts,
      recurringMaxCatchupOccurrences: finiteInt(
        finance.recurringMaxCatchupOccurrences,
        d.finance.recurringMaxCatchupOccurrences,
        1,
      ),
      exchangeRateMaxAgeDays: finiteInt(
        finance.exchangeRateMaxAgeDays,
        d.finance.exchangeRateMaxAgeDays,
      ),
    },
    ads: {
      interstitialPerSession: finiteInt(ads.interstitialPerSession, d.ads.interstitialPerSession),
      interstitialPerDay: finiteInt(ads.interstitialPerDay, d.ads.interstitialPerDay),
      minGapBetweenInterstitialsSecs: finiteInt(
        ads.minGapBetweenInterstitialsSecs,
        d.ads.minGapBetweenInterstitialsSecs,
      ),
      rewardsPerDayPerType: finiteInt(ads.rewardsPerDayPerType, d.ads.rewardsPerDayPerType),
    },
  };
}

/**
 * Budget thresholds as FRACTIONS (0..1), consumed by `resolveBudgetAlert`.
 * Config speaks in percentages because that is what a user enters in a panel;
 * the domain works in fractions.
 */
export function budgetAlertThresholds(config: PublicAppConfig): number[] {
  return config.finance.budgetAlertThresholdPcts.map((p) => p / FULL_PERCENT);
}

/** Ad capping in the format expected by `adGate({ policy })`. */
export function adCapPolicyOverrides(config: PublicAppConfig): {
  interstitialPerSession: number;
  interstitialPerDay: number;
  minGapBetweenInterstitialsMs: number;
  rewardsPerDayPerType: number;
} {
  return {
    interstitialPerSession: config.ads.interstitialPerSession,
    interstitialPerDay: config.ads.interstitialPerDay,
    // Config travels in SECONDS (human-readable unit in panel); gate works
    // in milliseconds. Conversion happens here, at a single point.
    minGapBetweenInterstitialsMs: config.ads.minGapBetweenInterstitialsSecs * MS_PER_SECOND,
    rewardsPerDayPerType: config.ads.rewardsPerDayPerType,
  };
}

/**
 * Is this exchange rate stale?
 *
 * `asOf` and `todayKey` are ISO `YYYY-MM-DD` keys (same format as the rest
 * of the domain). `maxAgeDays = 0` ⇒ **never** warn: historical behavior,
 * when this warning did not exist.
 *
 * ⚠️ This DOES NOT change any conversion. A recorded transaction keeps its
 * `base_amount` with the rate at the time; marking a rate as stale only warns
 * the user that NEW conversions might not be up-to-date.
 */
export function isExchangeRateStale(
  asOf: string | null | undefined,
  todayKey: string,
  maxAgeDays: number,
): boolean {
  if (maxAgeDays <= 0) {
    return false;
  }
  if (!asOf) {
    return true; // without a date there is no way to tell it is fresh
  }
  const asOfMs = Date.parse(`${asOf.slice(0, ISO_DATE_LENGTH)}T00:00:00Z`);
  const todayMs = Date.parse(`${todayKey.slice(0, ISO_DATE_LENGTH)}T00:00:00Z`);
  if (Number.isNaN(asOfMs) || Number.isNaN(todayMs)) {
    return false; // unreadable date: we do not invent an alarm
  }
  const ageDays = Math.floor((todayMs - asOfMs) / MS_PER_DAY);
  return ageDays > maxAgeDays;
}

/** TanStack query keys for public config. */
export const appConfigQueryKeys = {
  all: ["app-config"] as const,
  public: () => [...appConfigQueryKeys.all, "public"] as const,
  admin: () => [...appConfigQueryKeys.all, "admin"] as const,
};
